import React from 'react';
import { Card } from '@/components/ui/cards';
import { Text } from '@/components/ui/text/Text';
import { IconScout, IconScoutName } from '@/components/ui/images/IconScout';
import { colors } from '@/assets/styles/colors';


export interface CardNavItem {
  /** Stable identifier used to resolve the active item. */
  id: string;
  label: string;
  icon?: IconScoutName;
  'data-testid'?: string;
}

interface CardNavProps {
  items: CardNavItem[];
  /** Id of the item currently selected. */
  activeItemId: string;
  onItemClick: (id: string) => void;
  /** Accessible name for the navigation landmark. */
  ariaLabel?: string;
  className?: string;
  'data-testid'?: string;
}

interface CardNavButtonProps {
  item: CardNavItem;
  isActive: boolean;
  onClick: () => void;
}

const CardNavButton: React.FC<CardNavButtonProps> = ({ item, isActive, onClick }) => {
  const foreground = isActive ? colors.grays.neutralFF : colors.primary.dash;

  return (
    <Card
      elevation={1}
      borderRadius="12px"
      padding=""
      backgroundColor={isActive ? colors.important.main : colors.grays.neutralFF}
    >
      <button
        type="button"
        aria-current={isActive ? 'page' : undefined}
        className="flex h-14 items-center gap-2 px-4 whitespace-nowrap"
        onClick={onClick}
        data-testid={item['data-testid']}
      >
        {item.icon && <IconScout name={item.icon} size={20} color={foreground} />}
        <Text
          variant="body-regular"
          color={foreground}
          style={{ fontWeight: isActive ? 500 : 400, cursor: 'pointer' }}
        >
          {item.label}
        </Text>
      </button>
    </Card>
  );
};

/**
 * Horizontal navigation rendered as a row of cards, one of them highlighted as active.
 * Use it for switching between sibling sections of the same record, where each section is a
 * separate route. For switching content within a single page use `TabList` instead.
 */
export const CardNav: React.FC<CardNavProps> = ({
  items,
  activeItemId,
  onItemClick,
  ariaLabel,
  className = '',
  'data-testid': dataTestId,
}) => (
  <nav className={`flex gap-4 ${className}`} aria-label={ariaLabel} data-testid={dataTestId}>
    {items.map((item) => (
      <CardNavButton
        key={item.id}
        item={item}
        isActive={item.id === activeItemId}
        onClick={() => onItemClick(item.id)}
      />
    ))}
  </nav>
);

export default CardNav;
